import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  collection,
  onSnapshot,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import {
  Plug,
  RefreshCw,
  Loader2,
  Search,
  CheckCircle,
  Circle,
  AlertTriangle,
  Save,
} from 'lucide-react';
import { db as firestore, functions } from '../../lib/firebase';
import { PasteMondayTokenModal } from './PasteMondayTokenModal';

interface MondayBoard {
  id: string;
  name: string;
  workspaceId?: string | null;
  workspaceName?: string | null;
}

interface MondayConnectionDoc {
  connectionId: string;
  status: string;
  workspaceName?: string | null;
  accountEmail?: string | null;
  createdAt: string | null;
  selectedBoardIds: string[];
  knownBoards: MondayBoard[];
  lastSyncAt?: string | null;
  lastSyncResult?: string | null;
}

interface DiscoverBoardsRequest {
  companyId: string;
  connectionId: string;
}
interface DiscoverBoardsResponse {
  boards: MondayBoard[];
}

interface ConnectMondayRequest {
  companyId: string;
  code: string;
  redirectUri: string;
}
interface ConnectMondayResponse {
  success: boolean;
  connectionId: string;
}

interface DisconnectMondayRequest {
  companyId: string;
  connectionId: string;
}
interface DisconnectMondayResponse {
  success: boolean;
}

interface SaveBoardSelectionRequest {
  companyId: string;
  connectionId: string;
  boardIds: string[];
}
interface SaveBoardSelectionResponse {
  success: boolean;
  boardCount: number;
}

interface Props {
  companyId: string | undefined;
}

const MONDAY_AUTH_URL = 'https://auth.monday.com/oauth2/authorize';
const MONDAY_OAUTH_SCOPES = 'boards:read me:read users:read account:read';
const MONDAY_STATE_PREFIX = 'monday-';
const MONDAY_STATE_STORAGE_KEY = 'monday-oauth-state';

function formatDate(value: string | null | undefined): string {
  if (!value) return 'Never';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function tsToIso(value: unknown): string | null {
  if (!value) return null;
  if (value instanceof Timestamp) return value.toDate().toISOString();
  // Defensive — accept already-iso string from server-rendered docs.
  if (typeof value === 'string') return value;
  return null;
}

/**
 * Build the Monday.com OAuth authorize URL. `state` is a CSRF token
 * persisted to sessionStorage and verified by the OAuth callback handler
 * on `/leads/setup` before exchanging the code with the backend.
 */
function buildMondayAuthUrl(state: string, redirectUri: string): string {
  const clientId = process.env.REACT_APP_MONDAY_OAUTH_CLIENT_ID || '';
  const params = new URLSearchParams({
    client_id: clientId,
    redirect_uri: redirectUri,
    scope: MONDAY_OAUTH_SCOPES,
    response_type: 'code',
    state,
  });
  return `${MONDAY_AUTH_URL}?${params.toString()}`;
}

/**
 * Admin card on the Lead Capture Integrations page for Monday.com.
 * Live-reads `companies/{id}/crm_connections` filtered to provider=monday
 * via Firestore `onSnapshot`. The OAuth callback (code+state) is handled
 * by `LeadCaptureSetupWizardPage`'s mount effect, which calls
 * `connectMonday` and creates the connection doc — this card then sees the
 * doc appear via the live subscription.
 */
export const ConnectMondayCard: React.FC<Props> = ({ companyId }) => {
  const [connection, setConnection] = useState<MondayConnectionDoc | null>(null);
  const [loading, setLoading] = useState<boolean>(Boolean(companyId));
  const [pasteModalOpen, setPasteModalOpen] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [discoverError, setDiscoverError] = useState<string | null>(null);
  const [discoveredBoards, setDiscoveredBoards] = useState<MondayBoard[] | null>(null);
  const [pendingSelection, setPendingSelection] = useState<Set<string>>(new Set());
  const [savingSelection, setSavingSelection] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [disconnecting, setDisconnecting] = useState(false);

  // Live Firestore subscription — picks up doc creation from OAuth callback
  // or paste-token flow without a manual refresh.
  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const colRef = collection(firestore, `companies/${companyId}/crm_connections`);
    const q = query(colRef, where('provider', '==', 'monday'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        // Prefer an active connection; fall back to most recent doc so the
        // admin can still see "Disconnected" state with timestamps.
        const docs = snap.docs.map((d) => {
          const data = d.data();
          return {
            connectionId: d.id,
            status: typeof data.status === 'string' ? data.status : 'unknown',
            workspaceName:
              typeof data.workspaceName === 'string' ? data.workspaceName : null,
            accountEmail:
              typeof data.accountEmail === 'string' ? data.accountEmail : null,
            createdAt: tsToIso(data.createdAt),
            selectedBoardIds: Array.isArray(data.selectedBoardIds)
              ? (data.selectedBoardIds as string[])
              : [],
            knownBoards: Array.isArray(data.knownBoards)
              ? (data.knownBoards as MondayBoard[])
              : [],
            lastSyncAt: tsToIso(data.lastSyncAt),
            lastSyncResult:
              typeof data.lastSyncResult === 'string' ? data.lastSyncResult : null,
          } satisfies MondayConnectionDoc;
        });
        const active = docs.find((d) => d.status === 'active') ?? docs[0] ?? null;
        setConnection(active);
        setLoading(false);
      },
      (err) => {
        console.error('[ConnectMondayCard] snapshot error', err);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [companyId]);

  // Reset working selection whenever the persisted selection changes.
  useEffect(() => {
    if (connection) {
      setPendingSelection(new Set(connection.selectedBoardIds));
    } else {
      setPendingSelection(new Set());
    }
    setDiscoveredBoards(null);
    setDiscoverError(null);
    setSaveError(null);
  }, [connection?.connectionId, connection?.selectedBoardIds.join(',')]);

  const isConnected = connection?.status === 'active';
  const redirectUri = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return `${window.location.origin}/leads/setup`;
  }, []);

  // The list of boards available for selection: combine docs the backend has
  // already cached on the connection (`knownBoards`) with the freshly
  // discovered list, deduped by id.
  const boardCatalog = useMemo<MondayBoard[]>(() => {
    const map = new Map<string, MondayBoard>();
    for (const b of connection?.knownBoards ?? []) {
      map.set(b.id, b);
    }
    for (const b of discoveredBoards ?? []) {
      map.set(b.id, b);
    }
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [connection?.knownBoards, discoveredBoards]);

  const handleConnectOAuth = useCallback(() => {
    if (!companyId) return;
    const state = `${MONDAY_STATE_PREFIX}${crypto.randomUUID()}`;
    try {
      sessionStorage.setItem(
        MONDAY_STATE_STORAGE_KEY,
        JSON.stringify({ state, companyId, redirectUri })
      );
    } catch (err) {
      // sessionStorage may be unavailable in private mode — log but continue;
      // the callback handler will simply fail CSRF check and prompt retry.
      console.warn('[ConnectMondayCard] sessionStorage unavailable', err);
    }
    const authUrl = buildMondayAuthUrl(state, redirectUri);
    // Match the `window.open` pattern used by other ad-platform connect
    // flows. Falls back to current window if popup is blocked.
    const popup = window.open(
      authUrl,
      'monday-oauth',
      'width=600,height=700,popup=yes'
    );
    if (!popup) {
      window.location.href = authUrl;
    }
  }, [companyId, redirectUri]);

  const handleDiscoverBoards = useCallback(async () => {
    if (!companyId || !connection) return;
    setDiscovering(true);
    setDiscoverError(null);
    try {
      const fn = httpsCallable<DiscoverBoardsRequest, DiscoverBoardsResponse>(
        functions,
        'discoverMondayBoards'
      );
      const result = await fn({
        companyId,
        connectionId: connection.connectionId,
      });
      setDiscoveredBoards(result.data.boards);
    } catch (err) {
      setDiscoverError(err instanceof Error ? err.message : String(err));
    } finally {
      setDiscovering(false);
    }
  }, [companyId, connection]);

  const handleToggleBoard = useCallback((boardId: string) => {
    setPendingSelection((prev) => {
      const next = new Set(prev);
      if (next.has(boardId)) next.delete(boardId);
      else next.add(boardId);
      return next;
    });
  }, []);

  const selectionDirty = useMemo(() => {
    if (!connection) return false;
    const persisted = new Set(connection.selectedBoardIds);
    if (persisted.size !== pendingSelection.size) return true;
    for (const id of persisted) if (!pendingSelection.has(id)) return true;
    return false;
  }, [connection, pendingSelection]);

  const handleSaveSelection = useCallback(async () => {
    if (!companyId || !connection) return;
    setSavingSelection(true);
    setSaveError(null);
    try {
      const fn = httpsCallable<SaveBoardSelectionRequest, SaveBoardSelectionResponse>(
        functions,
        'saveMondayBoardSelection'
      );
      await fn({
        companyId,
        connectionId: connection.connectionId,
        boardIds: Array.from(pendingSelection),
      });
      // The Firestore subscription will reflect the saved state.
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : String(err));
    } finally {
      setSavingSelection(false);
    }
  }, [companyId, connection, pendingSelection]);

  const handleDisconnect = useCallback(async () => {
    if (!companyId || !connection) return;
    if (!window.confirm('Disconnect Monday.com? This stops scheduled syncs and removes the stored token.')) {
      return;
    }
    setDisconnecting(true);
    try {
      const fn = httpsCallable<DisconnectMondayRequest, DisconnectMondayResponse>(
        functions,
        'disconnectMonday'
      );
      await fn({
        companyId,
        connectionId: connection.connectionId,
      });
      // The live subscription clears the active doc.
    } catch (err) {
      console.error('[ConnectMondayCard] disconnect failed', err);
      window.alert(
        `Disconnect failed: ${err instanceof Error ? err.message : String(err)}`
      );
    } finally {
      setDisconnecting(false);
    }
  }, [companyId, connection]);

  return (
    <div
      className={`rounded-lg border ${
        isConnected
          ? 'border-emerald-500/30 bg-emerald-500/[0.03]'
          : 'border-gray-700 bg-gray-800/40'
      } p-5`}
    >
      <div className="flex items-start gap-3 mb-3">
        <div
          className="w-10 h-10 rounded flex items-center justify-center font-bold text-white flex-shrink-0"
          style={{ background: '#6c5ce7' }}
        >
          M
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-semibold text-white">
              Monday.com (Property)
            </h3>
            {isConnected ? (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-500/15 text-emerald-300 border border-emerald-500/30">
                <Circle className="w-2 h-2 fill-emerald-400" />
                Connected
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-500/15 text-gray-300 border border-gray-500/30">
                Not connected
              </span>
            )}
          </div>
          <p className="text-[11px] text-gray-500 mt-0.5">
            OAuth (Monday.com account) · {MONDAY_OAUTH_SCOPES}
          </p>
        </div>
      </div>
      <p className="text-xs text-gray-400 mb-4 leading-relaxed">
        Pull property deal data from Monday boards. Track cross-department
        pipeline from the property division alongside loan settlements.
      </p>

      {loading && (
        <div className="rounded-md border border-gray-700/50 bg-gray-900/40 px-3 py-2 mb-4 text-[11px] text-gray-400 inline-flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" />
          Loading connection state…
        </div>
      )}

      {isConnected && connection && (
        <div className="rounded-md border border-gray-700/50 bg-gray-900/40 px-3 py-2 space-y-1 mb-4 text-[11px]">
          {connection.workspaceName && (
            <div className="text-gray-400">
              <span className="text-gray-500">Workspace:</span>{' '}
              <span className="text-gray-200 font-medium">
                {connection.workspaceName}
              </span>
            </div>
          )}
          {connection.accountEmail && (
            <div className="text-gray-400">
              <span className="text-gray-500">Account:</span>{' '}
              <span className="font-mono text-gray-300">
                {connection.accountEmail}
              </span>
            </div>
          )}
          <div className="text-gray-400">
            <span className="text-gray-500">Connected:</span>{' '}
            <span className="text-gray-300">{formatDate(connection.createdAt)}</span>
          </div>
          <div className="text-gray-400">
            <span className="text-gray-500">Last sync:</span>{' '}
            <span className="text-gray-300">
              {formatDate(connection.lastSyncAt)}
            </span>
            {connection.lastSyncResult && (
              <span className="text-gray-500"> · {connection.lastSyncResult}</span>
            )}
          </div>
        </div>
      )}

      {isConnected && connection && (
        <div className="rounded-md border border-gray-700/50 bg-gray-900/40 p-3 mb-4">
          <div className="flex items-center justify-between mb-2 gap-2">
            <h4 className="text-xs font-semibold text-gray-200">
              Boards to sync
              {pendingSelection.size > 0 && (
                <span className="ml-2 text-[10px] font-normal text-gray-400">
                  ({pendingSelection.size} selected)
                </span>
              )}
            </h4>
            <button
              type="button"
              onClick={handleDiscoverBoards}
              disabled={discovering}
              className="px-2 py-1 rounded bg-gray-700 hover:bg-gray-600 text-white text-[11px] inline-flex items-center gap-1 disabled:opacity-50"
            >
              {discovering ? (
                <Loader2 className="w-3 h-3 animate-spin" />
              ) : (
                <Search className="w-3 h-3" />
              )}
              {discovering ? 'Discovering…' : 'Discover boards'}
            </button>
          </div>

          {discoverError && (
            <div className="rounded-md border border-red-500/30 bg-red-500/5 px-2 py-1.5 mb-2 text-[11px] text-red-300 flex items-start gap-1.5">
              <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
              {discoverError}
            </div>
          )}

          {boardCatalog.length === 0 ? (
            <p className="text-[11px] text-gray-500">
              No boards yet. Click <span className="font-semibold">Discover boards</span>{' '}
              to load the workspace catalog.
            </p>
          ) : (
            <div className="max-h-48 overflow-y-auto rounded border border-gray-700/50 divide-y divide-gray-800">
              {boardCatalog.map((board) => {
                const checked = pendingSelection.has(board.id);
                return (
                  <label
                    key={board.id}
                    className="flex items-center gap-2 px-2 py-1.5 hover:bg-gray-800/60 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleToggleBoard(board.id)}
                      className="w-3 h-3 accent-blue-600"
                    />
                    <span className="flex-1 min-w-0 text-[11px] text-gray-200 truncate">
                      {board.name}
                    </span>
                    {board.workspaceName && (
                      <span className="text-[10px] text-gray-500 truncate max-w-[120px]">
                        {board.workspaceName}
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          )}

          {saveError && (
            <div className="rounded-md border border-red-500/30 bg-red-500/5 px-2 py-1.5 mt-2 text-[11px] text-red-300 flex items-start gap-1.5">
              <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
              {saveError}
            </div>
          )}

          {selectionDirty && (
            <div className="flex justify-end mt-2">
              <button
                type="button"
                onClick={handleSaveSelection}
                disabled={savingSelection}
                className="px-2 py-1 rounded bg-blue-600 hover:bg-blue-700 text-white text-[11px] inline-flex items-center gap-1 disabled:opacity-50"
              >
                {savingSelection ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Save className="w-3 h-3" />
                )}
                {savingSelection ? 'Saving…' : 'Save board selection'}
              </button>
            </div>
          )}
        </div>
      )}

      <div className="flex items-center justify-between gap-2 flex-wrap">
        {isConnected ? (
          <div className="flex items-center gap-2 ml-auto">
            <button
              type="button"
              disabled
              title="Manual sync available in V2 — boards sync hourly via the scheduled job."
              className="px-3 py-1.5 rounded-md bg-gray-700/50 text-gray-500 text-xs inline-flex items-center gap-1.5 cursor-not-allowed"
            >
              <RefreshCw className="w-3 h-3" />
              Sync now
            </button>
            <button
              type="button"
              onClick={handleDisconnect}
              disabled={disconnecting}
              className="px-3 py-1.5 rounded-md bg-red-500/10 hover:bg-red-500/20 text-red-300 border border-red-500/30 text-xs disabled:opacity-50"
            >
              {disconnecting ? 'Disconnecting…' : 'Disconnect'}
            </button>
          </div>
        ) : (
          <>
            <button
              type="button"
              onClick={() => setPasteModalOpen(true)}
              disabled={!companyId}
              className="text-xs text-gray-400 hover:text-gray-200 underline-offset-2 hover:underline disabled:opacity-50"
            >
              Paste API token instead
            </button>
            <button
              type="button"
              onClick={handleConnectOAuth}
              disabled={!companyId}
              className="px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-xs inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Plug className="w-3 h-3" />
              Connect with OAuth
            </button>
          </>
        )}
      </div>

      {isConnected && connection && pendingSelection.size === 0 && (
        <div className="mt-3 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-[11px] text-amber-300 flex items-start gap-1.5">
          <AlertTriangle className="w-3 h-3 flex-shrink-0 mt-0.5" />
          No boards selected — the scheduled sync will skip this connection
          until at least one board is chosen.
        </div>
      )}

      {isConnected && connection && pendingSelection.size > 0 && !selectionDirty && (
        <div className="mt-3 rounded-md border border-emerald-500/30 bg-emerald-500/5 px-3 py-2 text-[11px] text-emerald-300 flex items-start gap-1.5">
          <CheckCircle className="w-3 h-3 flex-shrink-0 mt-0.5" />
          {pendingSelection.size} board{pendingSelection.size === 1 ? '' : 's'} configured
          for hourly sync.
        </div>
      )}

      <PasteMondayTokenModal
        companyId={companyId}
        open={pasteModalOpen}
        onClose={() => setPasteModalOpen(false)}
        onConnected={() => setPasteModalOpen(false)}
      />
    </div>
  );
};

export default ConnectMondayCard;
